import React, { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  X, Package, TrendingDown, TrendingUp, ArrowUpDown, BarChart3, Plus,
  ArrowDown, ArrowUp, Loader2, AlertTriangle, Pencil, Trash2, Check,
} from "lucide-react";

const fmtEur = (v) =>
  new Intl.NumberFormat("ca-ES", { style: "currency", currency: "EUR",
    minimumFractionDigits: 2, maximumFractionDigits: 4 })
    .format(Number(v) || 0);

const fmtNum = (v, digits = 2) =>
  new Intl.NumberFormat("ca-ES", {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(Number(v) || 0);

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("ca-ES", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return s; }
};

/**
 * Fullscreen product detail panel showing:
 *  - Product summary (stock, last price)
 *  - Stock movements timeline (last 50)
 *  - Per-supplier price comparison table with cheapest highlighted
 *  - Manual stock adjustment form
 */
export default function ProductDetail({ productId, onClose }) {
  const { lang } = useI18n();
  const [movs, setMovs] = useState([]);
  const [product, setProduct] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adjDelta, setAdjDelta] = useState("");
  const [adjPrice, setAdjPrice] = useState("");
  const [adjSupplier, setAdjSupplier] = useState("");
  const [adjNote, setAdjNote] = useState("");
  const [saving, setSaving] = useState(false);
  // Inline edit for one stock movement (id stored here)
  const [editingMovId, setEditingMovId] = useState(null);
  const [editMov, setEditMov] = useState({ delta: "", unit_price: "" });

  const startEditMov = (m) => {
    setEditingMovId(m.id);
    setEditMov({
      delta: String(m.delta ?? ""),
      unit_price: String(m.unit_price ?? ""),
    });
  };
  const cancelEditMov = () => {
    setEditingMovId(null);
    setEditMov({ delta: "", unit_price: "" });
  };
  const saveEditMov = async (m) => {
    try {
      await api.patch(`/products/${productId}/movements/${m.id}`, {
        delta: parseFloat(editMov.delta) || 0,
        unit_price: parseFloat(editMov.unit_price) || 0,
      });
      toast.success(lang === "es" ? "Movimiento actualizado" : "Moviment actualitzat");
      cancelEditMov();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };
  const deleteMov = async (m) => {
    const ok = window.confirm(
      lang === "es"
        ? `¿Eliminar el movimiento de ${m.delta > 0 ? "+" : ""}${m.delta} unidades?\n\nEl stock se recalculará automáticamente.`
        : `Eliminar el moviment de ${m.delta > 0 ? "+" : ""}${m.delta} unitats?\n\nL'estoc es recalcularà automàticament.`
    );
    if (!ok) return;
    try {
      await api.delete(`/products/${productId}/movements/${m.id}`);
      toast.success(lang === "es" ? "Movimiento eliminado" : "Moviment eliminat");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [m, c] = await Promise.all([
        api.get(`/products/${productId}/movements`, { params: { limit: 100 } }),
        api.get(`/products/${productId}/price-comparison`),
      ]);
      setMovs(m.data.movements || []);
      setProduct(m.data.product);
      setComparison(c.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [productId]); // eslint-disable-line

  const addAdjust = async () => {
    const delta = parseFloat(adjDelta);
    if (!delta || delta === 0) {
      toast.error(lang === "es" ? "Cantidad inválida" : "Quantitat no vàlida");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/products/${productId}/movements`, {
        delta, unit_price: parseFloat(adjPrice) || 0,
        supplier_name: adjSupplier, note: adjNote,
      });
      toast.success(lang === "es" ? "Ajuste registrado" : "Ajust registrat");
      setAdjDelta(""); setAdjPrice(""); setAdjSupplier(""); setAdjNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-150"
      onClick={onClose}
      data-testid="product-detail"
    >
      <div
        className="w-full sm:max-w-4xl max-h-[94vh] overflow-y-auto bg-[#0B1220] border-t sm:border border-white/10 sm:rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-[#0B1220] border-b border-white/10 p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <Package size={18} className="text-blue-300" />
            </div>
            <div className="min-w-0">
              <div className="text-xs text-slate-400 font-mono">{product?.code || "—"}</div>
              <h3 className="font-display text-lg font-semibold text-white truncate">
                {product?.name || "..."}
              </h3>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-white/5 text-slate-400 flex-shrink-0">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400">
            <Loader2 size={24} className="mx-auto mb-3 animate-spin" />
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryCard
                label={lang === "es" ? "Stock actual" : "Estoc actual"}
                value={`${fmtNum(product?.current_stock || 0)} ${product?.unit || ""}`}
                tint="from-emerald-500/15 to-emerald-500/0"
              />
              <SummaryCard
                label={lang === "es" ? "Último precio" : "Últim preu"}
                value={product?.last_price > 0 ? fmtEur(product.last_price) : "—"}
                tint="from-blue-500/15 to-blue-500/0"
              />
              <SummaryCard
                label={lang === "es" ? "Movimientos" : "Moviments"}
                value={movs.length}
                tint="from-violet-500/15 to-violet-500/0"
              />
              <SummaryCard
                label={lang === "es" ? "Proveedores" : "Proveïdors"}
                value={comparison?.suppliers?.length || 0}
                tint="from-amber-500/15 to-amber-500/0"
              />
            </div>

            {/* Price comparison */}
            <section data-testid="price-comparison-section">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h4 className="font-semibold text-white flex items-center gap-2">
                  <BarChart3 size={14} className="text-amber-300" />
                  {lang === "es" ? "Comparativa de precios por proveedor" : "Comparativa de preus per proveïdor"}
                </h4>
                <div className="flex items-center gap-3 text-xs">
                  {comparison?.saving_pct > 0 && (
                    <span className="text-emerald-300 font-semibold"
                          data-testid="cheapest-savings-badge">
                      {lang === "es"
                        ? `Ahorro entre proveedores: ${comparison.saving_pct}%`
                        : `Estalvi entre proveïdors: ${comparison.saving_pct}%`}
                    </span>
                  )}
                  {(() => {
                    const totalSaved = (comparison?.suppliers || [])
                      .filter((s) => s.negotiated_savings_pct > 0)
                      .reduce((acc, s) => acc + (s.last_list_price - s.last_price) * (s.total_qty || 0), 0);
                    return totalSaved > 0 ? (
                      <span className="px-2 py-0.5 rounded-md bg-violet-500/15 border border-violet-400/30 text-violet-200 font-semibold"
                            title={lang === "es"
                              ? "Diferencia acumulada entre el precio de catálogo y el precio efectivo gracias a regalos/descuentos"
                              : "Diferència acumulada entre el preu de catàleg i el preu efectiu gràcies a regals/descomptes"}
                            data-testid="catalog-vs-effective-savings">
                        {lang === "es" ? "Negociado: " : "Negociat: "}{fmtEur(totalSaved)}
                      </span>
                    ) : null;
                  })()}
                </div>
              </div>
              {!comparison?.suppliers || comparison.suppliers.length === 0 ? (
                <div className="text-xs text-slate-500 text-center py-6 border border-dashed border-white/10 rounded-lg">
                  {lang === "es"
                    ? "Aún no hay compras registradas. Añade un ajuste o valida una factura recibida."
                    : "Encara no hi ha compres registrades. Afegeix un ajust o valida una factura rebuda."}
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-white/10">
                  <table className="w-full text-xs">
                    <thead className="bg-white/[0.02]">
                      <tr className="uppercase tracking-wider text-slate-400 text-[10px]">
                        <th className="text-left p-2">{lang === "es" ? "Proveedor" : "Proveïdor"}</th>
                        <th className="text-right p-2">{lang === "es" ? "Compras" : "Compres"}</th>
                        <th className="text-right p-2">{lang === "es" ? "Cantidad" : "Quantitat"}</th>
                        <th className="text-right p-2"
                            title={lang === "es" ? "Precio de catálogo (antes de descuentos)" : "Preu de catàleg (abans de descomptes)"}>
                          {lang === "es" ? "Catálogo" : "Catàleg"}
                        </th>
                        <th className="text-right p-2"
                            title={lang === "es" ? "Precio efectivo medio (post-descuento)" : "Preu efectiu mitjà (post-descompte)"}>
                          {lang === "es" ? "Efectivo" : "Efectiu"}
                        </th>
                        <th className="text-right p-2">{lang === "es" ? "Mín." : "Mín."}</th>
                        <th className="text-right p-2">{lang === "es" ? "Máx." : "Màx."}</th>
                        <th className="text-right p-2">{lang === "es" ? "Último" : "Últim"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.suppliers.map((s, idx) => {
                        const isCheapest = comparison.cheapest && s.supplier_nif === comparison.cheapest.supplier_nif;
                        const isExpensive = comparison.most_expensive && s.supplier_nif === comparison.most_expensive.supplier_nif && comparison.suppliers.length > 1;
                        const hasNegotiated = (s.negotiated_savings_pct || 0) > 0;
                        const listPrice = s.avg_list_price || s.last_list_price || s.last_price;
                        return (
                          <tr key={idx} className={`border-t border-white/5 ${isCheapest ? "bg-emerald-500/5" : ""}`}
                              data-testid={`price-row-${idx}`}>
                            <td className="p-2">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {isCheapest && <TrendingDown size={11} className="text-emerald-400 flex-shrink-0" />}
                                {isExpensive && <TrendingUp size={11} className="text-rose-400 flex-shrink-0" />}
                                <span className="text-slate-200 font-medium">
                                  {s.supplier_name || s.supplier_nif || "—"}
                                </span>
                                {hasNegotiated && (
                                  <span className="px-1.5 py-0.5 rounded-md bg-violet-500/20 border border-violet-400/30 text-violet-200 text-[9px] font-bold leading-none"
                                        title={lang === "es"
                                          ? `Descuento medio negociado del ${s.negotiated_savings_pct}% (${fmtNum(s.total_free_units)} unidades gratis acumuladas)`
                                          : `Descompte mitjà negociat del ${s.negotiated_savings_pct}% (${fmtNum(s.total_free_units)} unitats gratis acumulades)`}
                                        data-testid={`negotiated-badge-${idx}`}>
                                    −{s.negotiated_savings_pct}%
                                  </span>
                                )}
                              </div>
                              {s.supplier_nif && (
                                <div className="text-[10px] text-slate-500 font-mono ml-4">{s.supplier_nif}</div>
                              )}
                            </td>
                            <td className="text-right p-2 text-slate-400 font-mono">{s.count}</td>
                            <td className="text-right p-2 text-slate-400 font-mono">{fmtNum(s.total_qty)}</td>
                            <td className="text-right p-2 font-mono text-slate-400 line-through decoration-violet-400/50"
                                style={{ textDecorationLine: hasNegotiated ? "line-through" : "none" }}>
                              {fmtEur(listPrice)}
                            </td>
                            <td className={`text-right p-2 font-mono font-semibold ${hasNegotiated ? "text-violet-200" : "text-slate-200"}`}>
                              {fmtEur(s.avg_price)}
                            </td>
                            <td className="text-right p-2 font-mono text-emerald-300">{fmtEur(s.min_price)}</td>
                            <td className="text-right p-2 font-mono text-rose-300">{fmtEur(s.max_price)}</td>
                            <td className={`text-right p-2 font-mono font-semibold ${isCheapest ? "text-emerald-300" : "text-white"}`}>
                              {fmtEur(s.last_price)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Manual adjustment */}
            <section className="border border-white/10 rounded-lg p-3 bg-white/[0.02]">
              <h4 className="text-xs font-semibold text-white mb-2 flex items-center gap-2">
                <Plus size={12} className="text-blue-300" />
                {lang === "es" ? "Ajuste manual de stock" : "Ajust manual d'estoc"}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <Input type="number" step="0.01" placeholder={lang === "es" ? "Cantidad (±)" : "Quantitat (±)"} value={adjDelta} onChange={(e) => setAdjDelta(e.target.value)} className="bg-white/5 border-white/10 font-mono text-xs h-8" data-testid="adj-delta" />
                <Input type="number" step="0.01" placeholder={lang === "es" ? "Precio €/u" : "Preu €/u"} value={adjPrice} onChange={(e) => setAdjPrice(e.target.value)} className="bg-white/5 border-white/10 font-mono text-xs h-8" data-testid="adj-price" />
                <Input placeholder={lang === "es" ? "Proveedor" : "Proveïdor"} value={adjSupplier} onChange={(e) => setAdjSupplier(e.target.value)} className="bg-white/5 border-white/10 text-xs h-8" />
                <Input placeholder={lang === "es" ? "Nota" : "Nota"} value={adjNote} onChange={(e) => setAdjNote(e.target.value)} className="bg-white/5 border-white/10 text-xs h-8" />
                <Button onClick={addAdjust} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8" data-testid="adj-save-btn">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : (lang === "es" ? "Añadir" : "Afegeix")}
                </Button>
              </div>
            </section>

            {/* Movements timeline */}
            <section>
              <h4 className="font-semibold text-white flex items-center gap-2 mb-2">
                <ArrowUpDown size={14} className="text-violet-300" />
                {lang === "es" ? "Historial de movimientos" : "Historial de moviments"}
              </h4>
              {movs.length === 0 ? (
                <div className="text-xs text-slate-500 text-center py-6 border border-dashed border-white/10 rounded-lg">
                  {lang === "es" ? "Sin movimientos" : "Sense moviments"}
                </div>
              ) : (
                <div className="space-y-1">
                  {movs.map((m, idx) => {
                    const isEditing = editingMovId === m.id;
                    return (
                    <div key={m.id || idx}
                         className={`flex items-center gap-2 p-2 rounded border bg-white/[0.02] ${m.price_alert ? "border-amber-500/40 bg-amber-500/[0.04]" : "border-white/5"}`}
                         data-testid={`mov-row-${idx}`}>
                      {m.delta > 0 ? <ArrowUp size={12} className="text-emerald-400 flex-shrink-0" /> : <ArrowDown size={12} className="text-rose-400 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-slate-200 truncate flex items-center gap-1.5">
                          {m.description || m.note || "—"}
                          {m.price_alert && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/30 flex-shrink-0"
                              title={lang === "es"
                                ? `Subida de precio: ${m.price_alert_pct}% vs media histórica ${fmtEur(m.price_alert_avg)}`
                                : `Pujada de preu: ${m.price_alert_pct}% vs mitjà històric ${fmtEur(m.price_alert_avg)}`}
                              data-testid={`mov-alert-${idx}`}
                            >
                              <AlertTriangle size={9} />
                              +{m.price_alert_pct}%
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-500">
                          {fmtDate(m.occurred_at)} · {m.supplier_name || m.supplier_nif || (lang === "es" ? "manual" : "manual")} · <span className="font-mono">{m.source_type}</span>
                        </div>
                      </div>
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number" step="0.01" autoFocus
                            value={editMov.delta}
                            onChange={(e) => setEditMov((s) => ({ ...s, delta: e.target.value }))}
                            className="bg-white/5 border-white/10 font-mono text-xs h-7 w-16 text-right"
                            placeholder="qty"
                            data-testid={`mov-edit-delta-${idx}`}
                          />
                          <Input
                            type="number" step="0.0001"
                            value={editMov.unit_price}
                            onChange={(e) => setEditMov((s) => ({ ...s, unit_price: e.target.value }))}
                            className="bg-white/5 border-white/10 font-mono text-xs h-7 w-20 text-right"
                            placeholder="€/u"
                            data-testid={`mov-edit-price-${idx}`}
                          />
                          <button
                            type="button" onClick={() => saveEditMov(m)}
                            className="p-1 rounded hover:bg-emerald-500/20 text-emerald-300"
                            title={lang === "es" ? "Guardar" : "Desar"}
                            data-testid={`mov-edit-save-${idx}`}
                          >
                            <Check size={14} />
                          </button>
                          <button
                            type="button" onClick={cancelEditMov}
                            className="p-1 rounded hover:bg-white/10 text-slate-400"
                            title={lang === "es" ? "Cancelar" : "Cancel·lar"}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="text-right">
                            <div className={`font-mono text-sm font-bold ${m.delta > 0 ? "text-emerald-300" : "text-rose-300"}`}>
                              {m.delta > 0 ? "+" : ""}{fmtNum(m.delta)}
                            </div>
                            {m.unit_price > 0 && (
                              <div className={`text-[10px] font-mono ${m.price_alert ? "text-amber-300 font-semibold" : "text-slate-400"}`}>
                                {fmtEur(m.unit_price)}/u
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-0.5 flex-shrink-0 ml-1">
                            <button
                              type="button" onClick={() => startEditMov(m)}
                              className="p-1 rounded hover:bg-white/10 text-slate-400 hover:text-blue-300 transition"
                              title={lang === "es" ? "Editar movimiento" : "Editar moviment"}
                              data-testid={`mov-edit-btn-${idx}`}
                            >
                              <Pencil size={12} />
                            </button>
                            <button
                              type="button" onClick={() => deleteMov(m)}
                              className="p-1 rounded hover:bg-white/10 text-slate-400 hover:text-rose-300 transition"
                              title={lang === "es" ? "Eliminar movimiento" : "Eliminar moviment"}
                              data-testid={`mov-delete-btn-${idx}`}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, tint }) {
  return (
    <div className={`p-3 rounded-lg border border-white/10 bg-gradient-to-br ${tint}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="text-xl font-bold text-white mt-1 font-mono truncate">{value}</div>
    </div>
  );
}
